import { useMemo, useState } from "react";
import { SectionShell } from "@/pages/SectionShell";
import { LegalTabs } from "./LegalTabs";
import { Button } from "@/components/Button";
import { TextField } from "@/components/Field";
import { EmptyState } from "@/components/EmptyState";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useCollection } from "@/lib/hooks/useCollection";
import { formatDateTime } from "@/lib/format";
import {
  CSV_COLUMNS,
  downloadCsv,
  exportFilename,
  matchesQuery,
  sortAcceptances,
  toCsv,
  type Acceptance,
} from "./acceptances";
import type { Institution } from "@/types";

/**
 * Who agreed to what, when, and from where.
 *
 * Read-only, and not because the buttons were left out: the rules refuse
 * create, update and delete on `legalAcceptances` outright. An acceptance is
 * evidence, and evidence anybody can edit is evidence of nothing.
 *
 * The export exists for a DPO request — somebody asks what their institution
 * signed and has to be answered in a form they can keep.
 */
export function AcceptancesPage() {
  const t = useT();
  const { locale } = useI18n();

  const { rows: acceptances, loading } = useCollection<Acceptance>("legalAcceptances");
  const { rows: institutions } = useCollection<Institution>("institutions");

  const [query, setQuery] = useState("");

  const nameOf = useMemo(() => {
    const names = new Map(institutions.map((institution) => [institution.id, institution.name]));
    return (id: string) => names.get(id) ?? id;
  }, [institutions]);

  const visible = useMemo(
    () => sortAcceptances(acceptances.filter((row) => matchesQuery(row, query, nameOf))),
    [acceptances, query, nameOf]
  );

  return (
    <SectionShell titleKey="legal.acceptances.title">
      <LegalTabs />

      <p className="max-w-3xl text-sm text-ink-soft">{t("legal.acceptances.intro")}</p>
      <p className="mt-2 max-w-3xl text-sm text-ink-muted">{t("legal.acceptances.immutable")}</p>

      <div className="mt-6 flex flex-wrap items-end gap-4">
        <TextField
          labelKey="legal.acceptances.search"
          hintKey="legal.acceptances.searchHint"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <Button
          variant="secondary"
          disabled={visible.length === 0}
          onClick={() =>
            downloadCsv(
              exportFilename(query, new Date().toISOString()),
              // Exactly what is on the screen, filter and all. An export that
              // quietly returned everything would answer a different question
              // from the one on the page.
              toCsv(visible, nameOf, (column) => t(`legal.acceptances.column.${column}`))
            )
          }
        >
          {t("legal.acceptances.export", { count: visible.length })}
        </Button>
      </div>

      <section className="mt-8">
        {loading ? (
          <p className="text-sm text-ink-muted">{t("common.loading")}</p>
        ) : visible.length === 0 ? (
          <EmptyState titleKey="legal.acceptances.empty" bodyKey="legal.acceptances.emptyBody" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">{t("legal.acceptances.title")}</caption>
              <thead>
                <tr className="border-b border-paper-deep text-left text-ink-muted">
                  {CSV_COLUMNS.map((column) => (
                    <th key={column} scope="col" className="px-3 py-2.5 font-medium">
                      {t(`legal.acceptances.column.${column}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => (
                  <tr key={row.id} className="border-b border-paper-warm last:border-0">
                    <td className="px-3 py-3 text-ink">{nameOf(row.institutionId)}</td>
                    <td className="px-3 py-3 font-mono text-xs text-ink-muted">
                      {row.institutionId}
                    </td>
                    <td className="px-3 py-3 font-mono text-xs text-ink-muted">{row.uid}</td>
                    <td className="px-3 py-3 text-ink-soft">{row.docId}</td>
                    <td className="px-3 py-3 text-ink">{row.version}</td>
                    <td className="px-3 py-3 text-ink-muted">
                      {formatDateTime(locale, row.acceptedAt)}
                    </td>
                    <td className="px-3 py-3 font-mono text-xs text-ink-muted">{row.ip}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </SectionShell>
  );
}
